import { UIRouterReact, UrlService } from '@uirouter/react';
import { SwLog } from '@similarweb/sw-log';
import { TrackingTool } from './types';
import {
    ITrack,
    AggregatedTracker,
    GoogleTracker,
    MixpanelTracker,
    BaseTracker,
    MatomoTracker, TrackModel
} from '@similarweb/sw-track';
import { logClientMessage, LogLevel } from '../Logging';

const router = new UIRouterReact();
const locationService = new UrlService(router);

// sw-log's levels don't match Identity's LogLevel names; entries omitted here (Info/Debug/Trace) are dropped by this override, not forwarded.
// These keys are verified against the pinned sw-track@5.2.8, which only ever passes Debug/Info — re-verify them if that pin moves, since an unmatched level is dropped silently.
const swLogLevelMap: Partial<Record<string, LogLevel>> = {
    Error: 'Error',
    Fatal: 'Critical',
    Warn: 'Warning'
};

const overrideSwLogServerLogger = (message: string, e?: Error, level = 'Error', logSource = 'frontend'): Promise<Response> => {
    const identityLevel = swLogLevelMap[level];

    if (identityLevel) {
        logClientMessage(message, identityLevel, {
            callstack: e?.stack ?? '',
            exception: e?.message ?? '',
            logSource,
            url: window.location.href
        });
    }

    return Promise.resolve(new Response(null, { status: 204 }));
};

// sw-log hardcodes fetch("/api/exception"), which Secure/Identity doesn't serve — drop health-check noise and forward real errors to /error/client instead.
SwLog.serverLogger = overrideSwLogServerLogger;

class CustomMatomoTracker extends MatomoTracker {
    protected paq(): any[] { return (window as any)._matomoPaq; }
    protected shouldBuffer(): boolean { return true; }
    protected searchParams() { return locationService.search(); }
    protected isEnabled(): boolean { return true; }
}

class CustomGoogleAnalyticsTracker extends GoogleTracker {
    protected ga(): (...params: any) => void { return (window as any).ga; }
    protected isEnabled(): boolean { return true; }
}

class CustomGoogleTagManagerTracker extends BaseTracker {
    protected dataLayer() { return (window as any).dataLayer; }
    protected isEnabled() { return true; }
    protected healthCheckInternal() { return null; }
    protected trackPageViewInternal() { return null; }
    protected trackEventInternal(trackModel:TrackModel) {
        const dataLayer = this.dataLayer();
        dataLayer && dataLayer.push({event: [trackModel.category, trackModel.action].join("/")});
    }
}

class CustomMixpanelTracker extends MixpanelTracker {
    protected mixpanel() { return (window as any).mixpanel; }
    protected isEnabled(): boolean { return true; }
}

const instances: Record<keyof typeof TrackingTool, ITrack> = {
    'Matomo': new CustomMatomoTracker() as ITrack,
    'GoogleAnalytics': new CustomGoogleAnalyticsTracker() as ITrack,
    'GoogleTagManager': new CustomGoogleTagManagerTracker() as ITrack,
    'Mixpanel': new CustomMixpanelTracker() as ITrack
};

export const initTrackers = (trackers: Array<keyof typeof TrackingTool>): void => {
    allTrackers = new AggregatedTracker(Object.keys(instances)
        .filter(x => trackers.includes(x as keyof typeof TrackingTool))
        .map(x => instances[x as keyof typeof TrackingTool])
    );
}

export let allTrackers = new AggregatedTracker([]);
